import { useMemo } from "react";
import type { Message } from "@ag-ui/client";

export interface ToolCallEntry {
  // toolCallId; stable across the call + result pair.
  id: string;
  name: string;
  // Raw JSON arguments string (may be partial while streaming).
  args: string;
  // Tool-result content. `undefined` while the call is still pending — the
  // disclosure renders the Output panel disabled in that state.
  result?: string;
}

export interface AssistantTurnData {
  kind: "assistant";
  key: string;
  toolCalls: ToolCallEntry[];
  // The *latest* assistant text-message in the turn. Agents emit interim
  // text-messages while thinking; the final one is the only one we render.
  text: string;
  isRunning: boolean;
  // Set when an assistant message in the turn carried the backend's
  // `cancelled: true` marker (the synthetic "[cancelled]" response written
  // when the user stops a run). Surfaces as an inline "Response cancelled"
  // line on the turn, both live and on reload.
  cancelled: boolean;
}

export interface UserTurnData {
  kind: "user";
  key: string;
  text: string;
}

export type Turn = UserTurnData | AssistantTurnData;

type AnyContent =
  | string
  | Array<{ type: string; text?: string } | Record<string, unknown>>
  | null
  | undefined;

function contentToText(content: AnyContent): string {
  if (!content) return "";
  if (typeof content === "string") return content;
  return content
    .map((part) =>
      typeof part === "object" && part && "type" in part && part.type === "text" && "text" in part
        ? String(part.text ?? "")
        : ""
    )
    .join("");
}

// Groups a flat AG-UI message list into per-turn render data.
//
// A turn boundary is a `user` message. Everything between two user messages
// (or after the last user message) forms one assistant turn that aggregates
// all tool calls + the latest assistant text. The same transform runs on both
// the streamed message list and the `/connect` snapshot, so live and replay
// render identically — including the persisted "[cancelled]" marker, which
// is surfaced via the per-message `cancelled` flag and bubbled onto the
// turn so the rendering side can show a "Response cancelled" line.
export function useTurns(messages: ReadonlyArray<Message>, isRunning: boolean): Turn[] {
  return useMemo(() => {
    const turns: Turn[] = [];
    let pending: AssistantTurnData | null = null;

    const flush = () => {
      if (pending && (pending.toolCalls.length > 0 || pending.text || pending.cancelled)) {
        turns.push(pending);
      }
      pending = null;
    };

    for (const m of messages) {
      if (m.role === "user") {
        flush();
        turns.push({
          kind: "user",
          key: m.id,
          text: contentToText(m.content as AnyContent),
        });
        continue;
      }
      if (m.role === "assistant") {
        if (!pending) {
          pending = {
            kind: "assistant",
            key: m.id,
            toolCalls: [],
            text: "",
            isRunning: false,
            cancelled: false,
          };
        }
        if (m.toolCalls?.length) {
          for (const tc of m.toolCalls) {
            pending.toolCalls.push({
              id: tc.id,
              name: tc.function.name,
              args: tc.function.arguments,
            });
          }
        }
        const text = contentToText(m.content as AnyContent);
        if (text) pending.text = text;
        // The backend sets `cancelled: true` on the synthetic marker message
        // it writes when the user stops a run. AG-UI's typed schema doesn't
        // model the field, but it survives the JSON round-trip on the wire.
        if ((m as { cancelled?: boolean }).cancelled) pending.cancelled = true;
        continue;
      }
      if (m.role === "tool") {
        if (!pending) continue;
        const entry = pending.toolCalls.find((t) => t.id === m.toolCallId);
        if (entry) entry.result = m.content;
      }
    }
    flush();

    if (isRunning) {
      const last = turns[turns.length - 1];
      if (!last || last.kind === "user") {
        // Fresh user prompt, agent hasn't emitted anything yet — show a
        // placeholder assistant turn so "Thinking..." is visible immediately.
        turns.push({
          kind: "assistant",
          key: "__pending__",
          toolCalls: [],
          text: "",
          isRunning: true,
          cancelled: false,
        });
      } else {
        last.isRunning = true;
      }
    }

    return turns;
  }, [messages, isRunning]);
}
